import React, { useState } from 'react';
import { PostActions } from '@/components/custom/post/post-actions';
import moment from 'moment';
import 'moment/locale/pt';
import api from '@/app/services/api.js';
import { useMutation } from 'jsonapi-react';
import toast from 'react-hot-toast';
import ReactHtmlParser from 'react-html-parser';
import { MagicLink } from '@/components/custom/magic-link';
import { FeedComment } from '@/app/screens/feed/feed-comment';
import getPermission from '@/app/utils/getPermission';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { PostCardBody, PostCardContent, PostCardHeader, PostCardName, PostCardText, PostCardTime, PostCardWrapper, PostCommentList } from '@/components/custom/post';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Loader } from '@/components/custom/loader';

export function FeedCard(props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { post } = props;
  const [text, setText] = useState('');
  const [isDeletingPost, setIsDeletingPost] = useState(false);
  const [addComment] = useMutation('comments');

  const createComment = e => {
    e.preventDefault();
    addComment({ body: text, post_id: post.id })
      .then()
      .then(response => {
        if (response.error) {
          toast.error(response.error.title);
        } else {
          setText('');
          props.addComments(response);
        }
      })
      .catch(error => {
        toast.error(t('toast.error'));
        console.error(error);
      });
  };

  const deletePost = post => {
    setIsDeletingPost(true);

    api
      .delete(`posts/${post.id}`)
      .then(response => {
        navigate('/feed');
        toast.success(t('toast.successDeleteMensage'));
      })
      .catch(error => {
        (toast.error(`Erro ao excluir menssagem: ${error}`), console.error(error));
      })
      .finally(() => {
        setIsDeletingPost(false);
      });
  };

  const deleteComment = (commentToDelete, setIsCommentLoading) => {
    Swal.fire({
      title: 'Deseja mesmo excluir este comentário?',
      text: 'Esta ação não poderá ser desfeita',
      confirmButtonText: 'Sim',
      showDenyButton: true,
      denyButtonText: 'Não',
      showCloseButton: true
    }).then(res => {
      if (res.isConfirmed) {
        if (setIsCommentLoading) setIsCommentLoading(true);

        api
          .delete(`comments/${commentToDelete.id}`)
          .then(response => {
            let newPost = { ...post };
            newPost.comments = newPost.comments.filter(c => c.id !== commentToDelete.id);
            newPost['comments-count'] = newPost.comments.length;

            props.setPost(newPost);
            toast.success(t('toast.successDeleteComment'));
          })
          .catch(error => {
            toast.error(`Erro ao excluir menssagem: ${error}`);
            console.error(error);
          })
          .finally(() => {
            if (setIsCommentLoading) setIsCommentLoading(false);
          });
      }
    });
  };

  const comments = post?.comments.filter(comment => comment['comment-parent-id'] === null);

  const handleNavigateToPost = (event, route, options = {}) => {
    event.stopPropagation();
    navigate(route, options);
  };

  if (isDeletingPost) {
    return (
      <PostCardWrapper className="flex h-40 items-center justify-center">
        <Loader />
      </PostCardWrapper>
    );
  }

  return (
    <PostCardWrapper>
      <AvatarWrapper
        size="sm"
        src={post?.user?.image?.url}
        text={post?.user?.name}
        onClick={event => handleNavigateToPost(event, `/usuario/metricas/${post['user-id']}`, { state: { isRedirected: true } })}
      />

      <PostCardContent>
        <PostCardHeader>
          <PostCardName onClick={event => handleNavigateToPost(event, `/usuario/metricas/${post['user-id']}`, { state: { isRedirected: true } })}>{post?.user?.name}</PostCardName>
          <PostCardTime>{moment(post['created-at']).fromNow()}</PostCardTime>
        </PostCardHeader>

        <PostCardBody>
          <PostCardText>{ReactHtmlParser(post?.body)}</PostCardText>
        </PostCardBody>

        {post['magic-links'] &&
          post['magic-links'].map(link => (
            <MagicLink
              key={link?.id}
              variant="post"
              magicLink={link}
            />
          ))}
      </PostCardContent>

      <PostActions
        post={post}
        deletePost={deletePost}
        isDeleting={isDeletingPost}
      />

      <PostCommentList>
        {comments?.map(comment => (
          <FeedComment
            key={comment?.id}
            comment={comment}
            hidePinnedBtn
            comments={post.comments}
            deleteCommentFromParent={deleteComment}
            setPost={props.setPost}
            className="last:border-b-0"
          />
        ))}
      </PostCommentList>

      {getPermission('Criar postagens e comentários', 'Fórum') && (
        <form
          className="w-full"
          method="post"
          onSubmit={e => createComment(e)}
        >
          <Input
            placeholder={t('textsCommon.placeholderSendYourComment')}
            aria-label={t('textsCommon.placeholderSendYourComment')}
            type="text"
            name="comment"
            id="comment"
            onChange={e => setText(e.target.value)}
            value={text}
            className="bg-bright"
          />
          <p className="text-right text-xs text-neutral">{t('textsCommon.textPressEnter')}</p>
        </form>
      )}
    </PostCardWrapper>
  );
}
